import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { trackSearchLoadEvent, EVENTS } from '../../../../../redux-stores/KeywordSearch/thunk';
import {
  isCurrentUser,
  renderBody,
  renderAvatar,
  getStoreModel,
  isScrollSuccessful,
} from './helper';
import BEM from 'common/bem';
import { User } from 'types';
import { ReactComponent as AttachmentIcon } from 'common/images/attach_file.svg';
import { ReactComponent as KeywordResultArrow } from 'common/images/keyword-result-arrow.svg';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';
import { actions, useAppDispatch } from 'redux-stores';
import waitForElement from 'common/utils/waitForElement';

const classes = BEM.with('KeywordSearchSideBar');

const { setSelectedMessageId, setSelectedHighlightedMessageId } = actions;

const TOP_PIXELS_SCROLL = 15;

type KeywordMessageBubbleMobXProps = {
  currentOrganizationId: string;
  getEntityData: (entityType: string, id: string) => User;
  setCurrentConversationId: (id: string) => void;
  navigateToConversation: (
    modelType: string,
    id: string,
    currentOrganizationId: string,
    sort_number: number
  ) => void;
  logInboxSearchGainsight: () => void;
  setEntityInboxSearchGainsight: (entity: { ['$entityType']: string }) => void;
  loadingImagesInProgress: boolean;
  isLoadingConversation: boolean;
  currentConversationId: string | null;
};

interface KeywordMessageBubbleProps {
  body: string;
  conversationId?: string;
  conversationMetadata: {
    peer_token: string;
    group_id: string;
    is_dl_list: boolean;
  };
  createdTime: string;
  currentUserId: string;
  highlight: {
    body: string;
    attachment_name: string | null;
  };
  isGroup: boolean;
  isSecondLevel?: boolean;
  id: string;
  messageId?: string;
  myRoleIds: string[];
  senderDisplayName?: string;
  senderToken: string;
  senderUserToken: string;
  senderRoleToken: string;
  sort_number: number;
}

const KeywordMessageBubble = ({
  body,
  currentOrganizationId,
  conversationMetadata,
  createdTime,
  currentUserId,
  getEntityData,
  highlight,
  isGroup,
  isSecondLevel,
  id,
  messageId,
  myRoleIds,
  senderDisplayName,
  senderToken,
  senderUserToken,
  senderRoleToken,
  sort_number,
  navigateToConversation,
  logInboxSearchGainsight,
  setEntityInboxSearchGainsight,
  loadingImagesInProgress,
  isLoadingConversation,
  currentConversationId,
}: KeywordMessageBubbleProps & KeywordMessageBubbleMobXProps) => {
  const isMe = isCurrentUser({
    currentUserId,
    senderToken,
    senderUserToken,
    senderRoleToken,
    myRoleIds,
  });
  const sender = getStoreModel('user', senderToken, getEntityData) as User;
  const [innerSelectedMessageId, setInnerSelectedMessageId] = useState('');
  const [prevContainerScrollTop, setPrevContainerScrollTop] = useState(0);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const scroll = async () => {
      if (innerSelectedMessageId !== '' && !loadingImagesInProgress && !isLoadingConversation) {
        const scrollContainer = document.getElementById('message-list-wrapper');
        const realScrollContainer = scrollContainer?.firstChild?.firstChild?.firstChild as Element;
        if (!realScrollContainer) return;
        const messageElement = await waitForElement(`[data-message-id="${messageId}"]`);

        if (!messageElement) {
          dispatch(trackSearchLoadEvent(EVENTS.MESSAGE_SCROLL_FAIL));
          return;
        }

        messageElement?.scrollIntoView();

        realScrollContainer.scrollTop = realScrollContainer.scrollTop - TOP_PIXELS_SCROLL;

        if (prevContainerScrollTop === realScrollContainer.scrollTop) {
          setInnerSelectedMessageId('');
        } else {
          setPrevContainerScrollTop(realScrollContainer.scrollTop);
        }

        const success = isScrollSuccessful(messageElement, realScrollContainer);
        if (success) {
          dispatch(trackSearchLoadEvent(EVENTS.MESSAGE_SCROLL_SUCCESS));
        } else {
          dispatch(trackSearchLoadEvent(EVENTS.MESSAGE_SCROLL_FAIL));
        }
      }
    };
    scroll();
  }, [
    isLoadingConversation,
    loadingImagesInProgress,
    innerSelectedMessageId,
    messageId,
    prevContainerScrollTop,
    currentConversationId,
    dispatch,
  ]);

  const openSelectedConversation = async () => {
    const { group_id, peer_token, is_dl_list } = conversationMetadata || {};
    const id = group_id || peer_token;
    const modelType = group_id ? 'group' : is_dl_list ? 'distributionList' : 'user';

    dispatch(trackSearchLoadEvent(EVENTS.OPEN_CONV_FOR_KWS_MESSAGE));

    await navigateToConversation(modelType, id, currentOrganizationId, sort_number);

    if (messageId) {
      setInnerSelectedMessageId(messageId);
      dispatch(setSelectedMessageId(messageId));
      dispatch(setSelectedHighlightedMessageId(messageId));
      setEntityInboxSearchGainsight({ $entityType: 'message' });
      logInboxSearchGainsight();
    }
  };

  return (
    <div className={classes('message-container')}>
      {(!isSecondLevel || (isSecondLevel && isGroup)) && (
        <div className={classes('avatar-container')}>
          {!isMe && <div className={classes('avatar')}>{renderAvatar(sender)}</div>}
        </div>
      )}
      <div
        className={classes('message-bubble', {
          isCurrentUser: isMe,
        })}
        onClick={openSelectedConversation}
        data-test-id={id}
      >
        {isGroup && !isMe && (
          <div className={classes('user-name')}>
            {senderDisplayName || sender?.displayName || 'Name not available'}
          </div>
        )}
        {renderBody(highlight.body, body)}
        {highlight.attachment_name && (
          <div className={classes('attachment-container')}>
            <AttachmentIcon />
            <div
              className={classes('attachment-name')}
              dangerouslySetInnerHTML={{ __html: highlight.attachment_name }}
            />
          </div>
        )}
        <div className={classes('message-created-time')}>
          {format(new Date(createdTime), 'M/d/yy h:mm a')}
        </div>
      </div>
      <div className={classes('arrow')} onClick={openSelectedConversation}>
        <KeywordResultArrow />
      </div>
    </div>
  );
};

export default mobxInjectSelect<KeywordMessageBubbleProps, KeywordMessageBubbleMobXProps>({
  conversationStore: [
    'navigateToConversation',
    'loadingImagesInProgress',
    'isLoadingConversation',
    'currentConversationId',
  ],
  entityStore: ['getEntityData'],
  messengerStore: ['currentOrganizationId'],
  trackerStore: ['logInboxSearchGainsight', 'setEntityInboxSearchGainsight'],
})(KeywordMessageBubble);
